import { effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { NpsCampaign, NPS_CAMPAIGN_BACKEND } from './nps-campaign-backend';

@Injectable({
  providedIn: 'root',
})
export class FeedbackCampaignService {
  private readonly backend = inject(NPS_CAMPAIGN_BACKEND);

  readonly campaign = signal<NpsCampaign | null>(null);
  readonly isVisible = signal(false);
  readonly isSubmitting = signal(false);

  private readonly dismissedThisSession = signal(false);

  constructor() {
    effect(() => {
      if (
        this.backend.shouldShowCampaign() &&
        !this.isVisible() &&
        !this.isSubmitting() &&
        !this.dismissedThisSession()
      ) {
        void this.loadCampaign();
      }
    });
  }

  dismissCurrentCampaign(doNotShowAgain: boolean): void {
    this.dismissedThisSession.set(true);
    if (doNotShowAgain) {
      const campaignId = this.campaign()?.id;
      if (campaignId) {
        void this.sendDoNotShowAgain(campaignId);
      }
    } else {
      this.closeWidget();
    }
  }

  skipCurrentCampaign(): void {
    this.dismissCurrentCampaign(false);
  }

  dontShowAgainCurrentCampaign(): void {
    this.dismissCurrentCampaign(true);
  }

  async submitCurrentCampaign(score: number, comment: string): Promise<boolean> {
    const campaignId = this.campaign()?.id;
    if (!campaignId) {
      return false;
    }

    this.isSubmitting.set(true);

    try {
      await firstValueFrom(this.backend.submitResponse(campaignId, score, comment));
      this.dismissedThisSession.set(true);
      this.closeWidget();
      return true;
    } catch (error) {
      console.error('[FeedbackCampaignService] Failed to submit campaign response.', error);
      return false;
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async sendDoNotShowAgain(campaignId: string): Promise<void> {
    this.isSubmitting.set(true);

    try {
      await firstValueFrom(this.backend.submitResponse(campaignId, null, ''));
      this.closeWidget();
    } catch (error) {
      console.error('[FeedbackCampaignService] Failed to send do-not-show-again.', error);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async loadCampaign(): Promise<void> {
    try {
      const campaign = await firstValueFrom(this.backend.getCampaign());
      if (!campaign) {
        return;
      }

      this.campaign.set(campaign);
      this.isVisible.set(true);
    } catch (error) {
      console.error('[FeedbackCampaignService] Failed to load campaign.', error);
    }
  }

  private closeWidget(): void {
    this.isVisible.set(false);
    this.campaign.set(null);
  }
}
